import type { IPropsMeta } from "../../..";
import type { ILFWCallback } from "../../../ILFWCallback";
import { PlayerInfo } from "../../../PlayerInfo";
import { FSM } from "../../../base/FSM";
import { CheatEnum, EntityGroup as EG, type IEntityData, TeamEnum } from "../../../defines";
import { Defines } from "../../../defines/defines";
import { Randoming } from "../../../helper";
import { between, max, min } from "../../../utils/math";
import type { IUIKeyEvent } from "../../IUIKeyEvent";
import { CharMenuHead } from "../CharMenuHead";
import { FighterName as CharMenuFighterName } from "../FighterName";
import { PlayerName as CharMenuPlayerName } from "../PlayerName";
import { PlayerTeamName as CharMenuPlayerTeamName } from "../PlayerTeamName";
import { UIComponent } from "../UIComponent";
import { CharMenuState } from "./CharMenuState";
import { CharMenuState_ComNumSel } from "./CharMenuState_ComNumSel";
import { CharMenuState_ComSel } from "./CharMenuState_ComSel";
import { CharMenuState_CountingDown } from "./CharMenuState_CountingDown";
import { CharMenuState_GameSetting } from "./CharMenuState_GameSetting";
import { CharMenuState_PlayerSel } from "./CharMenuState_PlayerSel";
import type { ICharMenuState } from "./ICharMenuState";
import type { ISlotPack } from "./ISlotPack";
import { SlotState } from "./SlotState";
import { SlotStep } from "./SlotStep";
export interface ICharMenuLogicProps {
  min_player?: number;
  max_player?: number;
  teams?: string[];
  /** 限定可选角色 OID 列表（如 B站生存排行只允许主线斗士） */
  oids?: string[];
  auto_join?: boolean;
}
/**
 * 角色选择逻辑
 *
 * @export
 * @class CharMenuLogic
 * @extends {UIComponent}
 */
export class CharMenuLogic extends UIComponent<ICharMenuLogicProps> {
  static override readonly TAGS: string[] = ["CharMenuLogic"];
  static override readonly PROPS: IPropsMeta<ICharMenuLogicProps> = {
    min_player: { type: Number, nullable: true },
    max_player: { type: Number, nullable: true },
    teams: { type: Array, items: String, nullable: true },
    oids: { type: Array, items: String, nullable: true },
    auto_join: { type: Boolean, nullable: true },
  };
  readonly prev_players = new Map<PlayerInfo, SlotState>()
  readonly players = new Map<PlayerInfo, SlotState>()
  protected _count_down: number = 5000;
  protected _randoming?: Randoming<IEntityData>;
  com_num: number = 0;

  get max_player(): number { return this.props.max_player ?? 8 }
  set max_player(v: number) { this.props.max_player = v }

  get min_player(): number { return this.props.min_player ?? 8 }
  set min_player(v: number) { this.props.min_player = v }

  get max_coms(): number { return Math.max(0, this.max_player - this.players.size) }
  get min_coms(): number { return Math.max(0, this.min_player - this.players.size) }

  get teams(): string[] { return this.props.teams ?? Defines.Teams.map(v => v.toString()) }
  set teams(v: string[]) { this.props.teams = v }

  get oids(): string[] | undefined { return this.props.oids }
  set oids(v: string[] | undefined) {
    this.props.oids = v
    // 限制名单变化后重建随机池，保证“随机”也从限制名单内挑
    if (this._randoming_ready) {
      this.rebuild_randoming()
      this.handle_fighters_changed()
    }
  }

  /** 限制在指定 OID 列表内 */
  protected filter_oids(list: readonly IEntityData[]): IEntityData[] {
    const { oids } = this;
    if (!oids?.length) return [...list];
    return list.filter(v => oids.includes('' + v.id))
  }

  protected _randoming_ready: boolean = false
  protected _randoming_fighters: number = -1

  protected rebuild_randoming(): void {
    const fighters = this.filter_oids(this.lfw.datas.get_fighters_of_group(EG.Regular))
    this._randoming = new Randoming(`charmenu_fighter_randoming`, fighters, this.lfw.mt)
    this._randoming_ready = true
    this._randoming_fighters = this.lfw.datas.fighters.length
  }

  protected ensure_randoming(): void {
    if (this._randoming_fighters === this.lfw.datas.fighters.length) return
    this.rebuild_randoming()
    for (const [, state] of this.players)
      if (!state.fighter) state.fighter = this._randoming?.get() ?? null
    this.update_slots()
  }

  get fighters(): readonly IEntityData[] {
    const cheat_0 = this.lfw.is_cheat(CheatEnum.LF2_NET);
    const cheat_1 = this.lfw.is_cheat(CheatEnum.GIM_INK);
    const all = this.lfw.datas.fighters;
    if (cheat_0 && cheat_1) return this.filter_oids(all)
    const ret = all.filter(v => {
      if (!cheat_0 && v.base.group?.some(v => v == EG.Hidden))
        return false;
      if (!cheat_1 && v.base.group?.some(v => v == EG.Dev))
        return false;
      return true
    })
    return this.filter_oids(ret.length ? ret : all);
  }
  protected _lf2_callbacks: ILFWCallback = {
    on_loading_end: () => this.ensure_randoming(),
    on_cheat_changed: (cheat_name, enabled) => {
      if (cheat_name === CheatEnum.LF2_NET && !enabled)
        this.handle_fighters_changed();
    },
    on_broadcast: (message) => {
      if (message === 'reset_gpl') return this.reset();
      if (message === 'update_random') return this.update_random();
      // UI 左/右按钮（见 survival_char_menu.ui.json5）：等价键盘 L/R 切换角色
      if (message === 'char_menu_pick_prev') return this.pick_lr(-1);
      if (message === 'char_menu_pick_next') return this.pick_lr(+1);
      if (message.startsWith('char_menu_pick_prev_')) return this.pick_lr(-1, +message.substring('char_menu_pick_prev_'.length) - 1);
      if (message.startsWith('char_menu_pick_next_')) return this.pick_lr(+1, +message.substring('char_menu_pick_next_'.length) - 1);
    }
  }
  slots: ISlotPack[] = []
  override on_start(): void {
    super.on_start?.();
    this.rebuild_randoming()
    this.lfw.callbacks.add(this._lf2_callbacks)
    const heads = this.node.search_components(CharMenuHead)
    const p_nam = this.node.search_components(CharMenuPlayerName)
    const f_nam = this.node.search_components(CharMenuFighterName)
    const t_nam = this.node.search_components(CharMenuPlayerTeamName)
    const len = max(
      heads.length,
      p_nam.length,
      f_nam.length,
      t_nam.length,
    )
    for (let i = 0; i < len; i++) {
      const e: ISlotPack = {
        head: heads.at(i),
        player_name: p_nam.at(i),
        fighter_name: f_nam.at(i),
        team_name: t_nam.at(i),
      }
      this.slots.push(e)
    }
    if (this.props.auto_join) this.auto_join()
  }

  override on_resume(): void {
    super.on_resume?.();
    if (this.props.auto_join) this.auto_join()
  }

  protected auto_join(): void {
    for (const p of this.players.keys()) if (!p.is_com) return
    for (const p of this.lfw.players.values()) {
      if (p.is_com) continue
      this.press_a(p)
      return
    }
  }

  override on_stop(): void {
    super.on_stop?.();
    this.lfw.callbacks.del(this._lf2_callbacks)
  }
  override on_key_down(e: IUIKeyEvent): void {
    this.fsm.state?.on_key_down?.(e)
  }
  reset() {
    this.prev_players.clear()
    for (const [k, v] of this.players)
      this.prev_players.set(k, v);
    this.players.clear();
    this.update_slots()
    this.fsm.use(CharMenuState.PlayerSel)
  }
  update_random() {
    this.ensure_randoming();
    for (const [_, state] of this.players) {
      if (!state.random) continue;
      state.fighter = this._randoming?.get() ?? null
    }
    this.update_slots()
  }

  /** 取消倒计时（按“防御”）：退回选角步骤并回到选角状态 */
  cancel_counting_down(player: PlayerInfo): void {
    const state = this.players.get(player)
    if (!state) return
    state.step = SlotStep.FighterSel
    this.lfw.sounds.play_preset("cancel")
    this.fsm.use(CharMenuState.PlayerSel)
  }
  update_slots() {
    const { slots } = this;
    const players = Array.from(this.players.entries())
    for (let i = 0; i < slots.length; i++) {
      const { head, player_name, fighter_name, team_name } = slots[i];
      const ps = players.at(i)
      if (!ps) {
        if (head) head.quit();
        if (player_name) player_name.quit();
        if (fighter_name) fighter_name.quit();
        if (team_name) team_name.quit();
        continue;
      }
      const [player, state] = ps
      const { is_com } = player;
      const random_confirm = this.fsm.state?.key === CharMenuState.GameSetting
      if (head) {
        if (state.random && !random_confirm) head.join(Defines.BuiltIn_Imgs.RFACE)
        else if (state.fighter) head.join(state.fighter.base.head ?? Defines.BuiltIn_Imgs.RFACE)
      }
      if (player_name) {
        player_name.join(player.name, is_com, true)
      }
      if (fighter_name) {
        const decided = state.step > SlotStep.FighterSel
        if (state.random && !random_confirm) fighter_name.join(this.lfw.string('Random'), is_com, decided)
        else if (state.fighter) fighter_name.join(this.lfw.string(state.fighter.base.name) || "noname", is_com, decided)
      }
      if (team_name) {
        if (state.step >= SlotStep.TeamSel) {
          const team_info = Defines.TeamInfoMap[state.team]
          const team_txt = this.lfw.string(team_info?.i18n ?? state.team)
          team_name.join(team_txt, is_com, state.step > SlotStep.TeamSel)
        } else {
          team_name.quit()
        }
      }
    }
  }
  press_a(player: PlayerInfo) {
    this.ensure_randoming();
    const state = this.players.get(player)
    if (!state && this.max_player <= this.players.size)
      return;
    if (!state) {
      const slot_state = this.prev_players.get(player) ?? new SlotState({
        team: this.teams[0] ?? TeamEnum.Independent,
        fighter: this._randoming?.get() ?? null
      })
      slot_state.step = SlotStep.FighterSel;
      this.prev_players.delete(player)
      this.players.set(player, slot_state);
    } else if (state.step < SlotStep.Ready) {
      if (state.step + 1 === SlotStep.TeamSel && this.teams.length <= 1) {
        state.step = SlotStep.Ready;
      } else {
        state.step = min(state.step + 1, SlotStep.Ready)
      }
    } else return;

    this.lfw.sounds.play_preset("join");
    this.update_slots()
  }
  get is_player_sel(): boolean { return this.fsm.state?.key === CharMenuState.PlayerSel }
  press_j(player: PlayerInfo) {
    const state = this.players.get(player)
    if (!state) { if (this.is_player_sel) this.node.pop_page({ min_pages: 1 }); }
    else if (state.step <= SlotStep.FighterSel) {
      // auto_join：不取消加入（避免退回“按攻击加入”的未加入态），按“跳跃”直接返回上一页
      if (this.props.auto_join) {
        this.lfw.sounds.play_preset("cancel")
        this.node.pop_page({ min_pages: 1 })
        return
      }
      this.players.delete(player);
    }
    else state.step = max(state.step - 1, SlotStep.FighterSel)
    this.lfw.sounds.play_preset("cancel");
    this.update_slots()
  }

  press_lr(player: PlayerInfo, dir: 1 | -1) {
    const state = this.players.get(player)
    if (!state) return;
    if (state.step === SlotStep.FighterSel) {
      const { fighters } = this;
      if (!fighters.length) return;

      if (state.random === true || !state.fighter) {
        state.random = false;
        state.fighter = dir < 0 ? fighters[fighters.length - 1] : fighters[0]
      } else {
        const next_idx = fighters.indexOf(state.fighter) + dir
        if (between(next_idx, 0, fighters.length - 1)) {
          state.random = false;
          state.fighter = fighters[next_idx];
        } else {
          state.random = true;
          state.fighter = this._randoming?.get() ?? null;
        }
      }
    } else if (state.step === SlotStep.TeamSel) {
      const { teams } = this;
      const len = teams.length
      const next_idx = (len + teams.indexOf(state.team) + dir) % len;
      state.team = teams[next_idx]
    }
    this.update_slots()
  }

  /**
   * UI 左/右按钮：对菜单内的本地玩家切换角色（等价键盘 L/R，仅在选角状态生效）。
   * 还没有玩家加入时，先以首位本地玩家加入，再切换。
   * 传 index 时只作用于该槽位的玩家（双人页左右两列各自的按钮）。
   */
  pick_lr(dir: 1 | -1, index?: number): void {
    if (!this.is_player_sel) return
    let player: PlayerInfo | undefined
    if (index === void 0) {
      for (const p of this.players.keys()) {
        if (p.is_com) continue
        player = p
        break
      }
      if (!player) {
        for (const p of this.lfw.players.values()) {
          if (p.is_com) continue
          player = p
          break
        }
        if (!player) return
        this.press_a(player)
      }
    } else {
      player = Array.from(this.players.keys())[index]
      if (!player) {
        player = Array.from(this.lfw.players.values()).filter(v => !v.is_com)[index]
        if (!player) return
        this.press_a(player)
      }
    }
    this.press_lr(player, dir)
  }

  press_u(player: PlayerInfo) {
    const state = this.players.get(player)
    if (state?.step !== SlotStep.FighterSel) return
    state.random = true;
    state.fighter = this._randoming?.get() ?? null;
    this.update_slots();

  }

  /**
   * 当前选择的角色被隐藏时，让玩家选随机
   *
   * @protected
   */
  protected handle_fighters_changed() {
    const { fighters } = this;
    for (const [, s] of this.players) {
      const hidden = !fighters.some(v => v === s.fighter)
      if (!hidden) continue;
      s.random = true;
      s.fighter = null;
    }
    this.update_slots()
  }
  protected _player_names_key = '';
  override update(dt: number): void {
    this.fsm.update(dt)
    let key = '';
    for (const p of this.players.keys()) key += `${p.id}\u0000${p.name}\n`;
    if (key === this._player_names_key) return;
    this._player_names_key = key;
    this.update_slots();
  }
  readonly fsm = new FSM<CharMenuState, ICharMenuState>().add(
    new CharMenuState_PlayerSel(this),
    new CharMenuState_CountingDown(this),
    new CharMenuState_ComNumSel(this),
    new CharMenuState_ComSel(this),
    new CharMenuState_GameSetting(this),
  ).use(CharMenuState.PlayerSel)

  last_player(): [PlayerInfo, SlotState] | undefined {
    const pairs = Array.from(this.players.entries())
    return pairs.at(pairs.length - 1)
  }
  last_com(): [PlayerInfo, SlotState] | undefined {
    const ret = this.last_player();
    if (!ret?.[0].is_com) return void 0;
    return ret;
  }
  add_com() {
    if (this.max_player <= this.players.size) return;

    let com: PlayerInfo | null = null
    for (const [_, p] of this.lfw.players) {
      if (!p.is_com) continue;
      if (p.is_com && this.players.has(p)) continue;
      com = p;
    }
    if (!com) {
      com = new PlayerInfo(`com_` + this.lfw.new_id, "com", false)
      com.set_is_com(true, false);
      this.lfw.players.set(com.id, com)
    }
    this.press_a(com)
  }
}

